import { useState, Fragment } from 'react'
import { useCarriers } from '../hooks/useCarriers'
import { useNotification } from '../contexts/NotificationContext'
import { parseApiError } from '../utils/errorUtils'
import { Skeleton, CarrierTableSkeleton } from '../components/skeleton'
import './Carriers.css'

function Carriers() {
  const {
    carriers,
    isLoading,
    error,
    createCarrier,
    updateCarrier,
    deleteCarrier,
    refetch
  } = useCarriers()
  const { showSuccess, showError } = useNotification()

  const [searchTerm, setSearchTerm] = useState('')
  const [expandedRow, setExpandedRow] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCarrier, setEditingCarrier] = useState(null) // Track carrier being edited
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [newCarrier, setNewCarrier] = useState({
    name: '',
    type: '',
    icon: '',
    contactTypes: [],
    primaryContactType: '',
    contacts: {
      email: { address: '', name: '' },
      phone: { number: '', name: '' },
      website: { url: '', username: '', password: '' },
      api: { url: '', headers: '' }
    }
  })

  // Filter carriers based on search
  const filteredCarriers = carriers.filter(carrier =>
    carrier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    carrier.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    carrier.contactInfo?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id)
  }

  const handleToggleContactType = (type) => {
    const currentTypes = newCarrier.contactTypes || []
    let updatedTypes
    let updatedPrimary = newCarrier.primaryContactType

    if (currentTypes.includes(type)) {
      updatedTypes = currentTypes.filter(t => t !== type)
      // Clear primary if the removed type was the primary
      if (updatedPrimary === type) {
        updatedPrimary = updatedTypes.length === 1 ? updatedTypes[0] : ''
      }
    } else {
      updatedTypes = [...currentTypes, type]
      // Auto-select primary if it's the first/only type
      if (updatedTypes.length === 1) {
        updatedPrimary = type
      }
    }

    setNewCarrier({
      ...newCarrier,
      contactTypes: updatedTypes,
      primaryContactType: updatedPrimary,
    })
  }

  const handleContactChange = (type, field, value) => {
    setNewCarrier({
      ...newCarrier,
      contacts: {
        ...newCarrier.contacts,
        [type]: {
          ...newCarrier.contacts[type],
          [field]: value
        }
      }
    })
  }

  const resetForm = () => {
    setNewCarrier({
      name: '',
      type: '',
      icon: '',
      contactTypes: [],
      primaryContactType: '',
      contacts: {
        email: { address: '', name: '' },
        phone: { number: '', name: '' },
        website: { url: '', username: '', password: '' },
        api: { url: '', headers: '' }
      }
    })
    setEditingCarrier(null)
  }

  // Open edit modal with carrier data populated
  const handleEditCarrier = (carrier) => {
    // Determine contact types based on available data
    // Check both contactType AND the raw field values from transformer
    const contactTypes = []

    // Check for real email (not placeholder)
    const hasRealEmail = carrier.email && !carrier.email.endsWith('@carrier.local')
    if (carrier.contactType === 'email' || hasRealEmail) contactTypes.push('email')

    // Check for real phone (not placeholder)
    const hasRealPhone = carrier.phone && carrier.phone !== '+10000000000'
    if (carrier.contactType === 'phone' || hasRealPhone) contactTypes.push('phone')

    // Check for website
    if (carrier.contactType === 'website' || carrier.website) contactTypes.push('website')

    // Check for API
    if (carrier.contactType === 'api' || carrier.api) contactTypes.push('api')

    // Map display type back to form select value
    // Display shows "Ocean Freight", "Air Freight", etc. but select uses "Sea", "Air", etc.
    const typeMapping = {
      'Ocean Freight': 'Sea',
      'Air Freight': 'Air',
      'Rail': 'Rail',
      'Road': 'Road',
    }
    const formType = typeMapping[carrier.type] || carrier.type || ''

    setNewCarrier({
      name: carrier.name || '',
      type: formType,
      icon: carrier.icon || '',
      contactTypes,
      primaryContactType: carrier.contactType || (contactTypes.length === 1 ? contactTypes[0] : ''),
      contacts: {
        email: {
          address: hasRealEmail ? carrier.email : '',
          name: carrier.contactName || ''
        },
        phone: {
          number: hasRealPhone ? carrier.phone : '',
          name: carrier.contactName || ''
        },
        website: {
          url: carrier.website || '',
          username: '',
          password: ''
        },
        api: {
          url: carrier.api || '',
          headers: carrier.apiHeaders ? JSON.stringify(carrier.apiHeaders, null, 2) : ''
        }
      }
    })
    setEditingCarrier(carrier)
    setShowAddModal(true)
  }

  const handleSaveCarrier = async () => {
    const hasValidContact = newCarrier.contactTypes.some(type => {
      if (type === 'email') return newCarrier.contacts.email.address?.trim() && newCarrier.contacts.email.name?.trim()
      if (type === 'phone') return newCarrier.contacts.phone.number?.trim() && newCarrier.contacts.phone.name?.trim()
      if (type === 'website') return newCarrier.contacts.website.url?.trim()
      if (type === 'api') return newCarrier.contacts.api.url?.trim()
      return false
    })

    if (!newCarrier.name || !newCarrier.type || newCarrier.contactTypes.length === 0 || !hasValidContact || !newCarrier.primaryContactType) {
      showError('Please fill in all required fields and select a primary contact type')
      return
    }

    setIsSubmitting(true)
    try {
      if (editingCarrier) {
        // Update existing carrier
        await updateCarrier(editingCarrier.id, newCarrier)
        showSuccess(`Carrier "${newCarrier.name}" updated successfully!`)
      } else {
        // Create new carrier
        await createCarrier(newCarrier)
        showSuccess(`Carrier "${newCarrier.name}" added successfully!`)
      }
      setShowAddModal(false)
      resetForm()
    } catch (err) {
      console.error('[Carriers] Save failed:', err)
      const parsed = parseApiError(err)
      showError(parsed.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getContactLabel = (contactType) => {
    switch(contactType) {
      case 'email': return 'Email'
      case 'phone': return 'Phone'
      case 'website': return 'Website'
      case 'api': return 'API'
      default: return 'Contact'
    }
  }

  const getContactIcon = (contactType) => {
    switch(contactType) {
      case 'email': return '📧'
      case 'phone': return '📞'
      case 'website': return '🌐'
      case 'api': return '🔗'
      default: return '📋'
    }
  }

  const handleDeleteCarrier = async (id, name) => {
    if (!window.confirm(`Are you sure you want to remove "${name}" from your registry?`)) {
      return
    }

    try {
      await deleteCarrier(id)
      showSuccess(`Carrier "${name}" removed successfully`)
    } catch (err) {
      console.error('[Carriers] Delete failed:', err)
      const parsed = parseApiError(err)
      showError(parsed.message)
    }
  }

  const handleContact = (carrier) => {
    switch(carrier.contactType) {
      case 'email':
        window.location.href = `mailto:${carrier.contactInfo}`
        break
      case 'phone':
        window.location.href = `tel:${carrier.contactInfo}`
        break
      case 'website':
        window.open(carrier.contactInfo, '_blank')
        break
      default:
        break
    }
  }

  // Loading state - show skeleton layout
  if (isLoading) {
    return (
      <div className="carriers-page">
        <div className="page-header">
          <div className="header-content">
            <h1>Carrier Registry</h1>
            <p>Manage your freight forwarding and logistics carriers</p>
          </div>
          <Skeleton variant="rectangular" width="170px" height="48px" />
        </div>

        <div className="search-filter-section">
          <div className="search-bar">
            <Skeleton variant="rectangular" width="100%" height="52px" />
          </div>
          <Skeleton variant="text" width="180px" height="0.875rem" />
        </div>

        <CarrierTableSkeleton rows={5} />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="carriers-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <h3>Failed to load carriers</h3>
          <p>{error.message}</p>
          <button className="btn-retry" onClick={refetch}>
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="carriers-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Carrier Registry</h1>
          <p>Manage your freight forwarding and logistics carriers</p>
        </div>
        <button className="btn-add-carrier" onClick={() => setShowAddModal(true)}>
          <span className="btn-icon">+</span>
          Add New Carrier
        </button>
      </div>

      <div className="search-filter-section">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search carriers by name, type, or contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="results-count">
          Showing {filteredCarriers.length} of {carriers.length} carriers
        </div>
      </div>

      <div className="table-container">
        {filteredCarriers.length > 0 ? (
          <table className="carriers-table">
            <thead>
              <tr>
                <th>Icon</th>
                <th>Carrier Name</th>
                <th>Freight Type</th>
                <th>Contact Type</th>
                <th>Primary Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCarriers.map(carrier => (
                <Fragment key={carrier.id}>
                  <tr className={expandedRow === carrier.id ? 'expanded' : ''}>
                    <td className="icon-cell">
                      <span className="carrier-icon-large">{carrier.icon}</span>
                    </td>
                    <td className="name-cell">
                      <strong>{carrier.name}</strong>
                    </td>
                    <td>
                      <span className="type-badge">{carrier.type}</span>
                    </td>
                    <td>
                      <span className="contact-type">
                        {getContactIcon(carrier.contactType)} {getContactLabel(carrier.contactType)}
                      </span>
                    </td>
                    <td className="contact-cell">
                      <span className="contact-info">{carrier.contactInfo}</span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="btn-details"
                        onClick={() => toggleRow(carrier.id)}
                      >
                        {expandedRow === carrier.id ? '▼ Hide' : '▶ Details'}
                      </button>
                    </td>
                  </tr>
                  {expandedRow === carrier.id && (
                    <tr className="details-row">
                      <td colSpan="6">
                        <div className="details-content">
                          <div className="details-grid">
                            <div className="detail-section">
                              <h4>Carrier Information</h4>
                              <div className="detail-items">
                                <div className="detail-item">
                                  <span className="detail-label">Carrier Name:</span>
                                  <span className="detail-value">{carrier.name}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Freight Type:</span>
                                  <span className="detail-value">{carrier.type}</span>
                                </div>
                              </div>
                            </div>

                            <div className="detail-section">
                              <h4>Contact Details</h4>
                              <div className="detail-items">
                                <div className="detail-item">
                                  <span className="detail-label">Contact Type:</span>
                                  <span className="detail-value">
                                    {getContactIcon(carrier.contactType)} {getContactLabel(carrier.contactType)}
                                  </span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Contact Info:</span>
                                  <span className="detail-value highlight">{carrier.contactInfo}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="details-actions">
                            {carrier.contactType !== 'email' && (
                              <button
                                className="btn-contact"
                                onClick={() => handleContact(carrier)}
                              >
                                {carrier.contactType === 'phone' && '📞 Call Now'}
                                {carrier.contactType === 'website' && '🌐 Visit Website'}
                                {carrier.contactType === 'api' && '🔗 API Endpoint'}
                              </button>
                            )}
                            <button
                              className="btn-view"
                              onClick={() => handleEditCarrier(carrier)}
                            >
                              Edit Carrier
                            </button>
                            <button
                              className="btn-delete-action"
                              onClick={() => handleDeleteCarrier(carrier.id, carrier.name)}
                            >
                              🗑️ Remove Carrier
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <h3>No carriers found</h3>
            <p>{carriers.length === 0 ? 'Add your first carrier to get started' : 'Try adjusting your search'}</p>
            {carriers.length === 0 && (
              <button className="btn-add-carrier" onClick={() => setShowAddModal(true)}>
                <span className="btn-icon">+</span>
                Add First Carrier
              </button>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Carrier Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { if (!isSubmitting) { setShowAddModal(false); resetForm(); } }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCarrier ? 'Edit Carrier' : 'Add New Carrier'}</h3>
              <button
                className="modal-close"
                onClick={() => { setShowAddModal(false); resetForm(); }}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-form-group">
                <label>Carrier Name *</label>
                <input
                  type="text"
                  value={newCarrier.name}
                  onChange={(e) => setNewCarrier({...newCarrier, name: e.target.value})}
                  placeholder="e.g., ABC Logistics"
                  disabled={isSubmitting}
                />
              </div>
              <div className="modal-form-row">
                <div className="modal-form-group">
                  <label>Freight Type *</label>
                  <select
                    value={newCarrier.type}
                    onChange={(e) => setNewCarrier({...newCarrier, type: e.target.value})}
                    disabled={isSubmitting}
                  >
                    <option value="">Select freight type...</option>
                    <option value="Air">Air</option>
                    <option value="Rail">Rail</option>
                    <option value="Road">Road</option>
                    <option value="Sea">Sea</option>
                  </select>
                </div>
                <div className="modal-form-group icon-group">
                  <label>Icon</label>
                  <select
                    className="icon-select"
                    value={newCarrier.icon}
                    onChange={(e) => setNewCarrier({...newCarrier, icon: e.target.value})}
                    disabled={isSubmitting}
                  >
                    <option value="">(Auto)</option>
                    <option value="🚢">🚢</option>
                    <option value="⚓">⚓</option>
                    <option value="🛳">🛳</option>
                    <option value="⛴">⛴</option>
                    <option value="✈">✈</option>
                    <option value="🛫">🛫</option>
                    <option value="🚚">🚚</option>
                    <option value="📦">📦</option>
                    <option value="🏭">🏭</option>
                    <option value="📮">📮</option>
                    <option value="🌐">🌐</option>
                  </select>
                </div>
              </div>
              <div className="modal-form-group">
                <label>Contact Types * (Select one or more)</label>
                <div className="contact-type-options">
                  <label className="contact-type-checkbox">
                    <input
                      type="checkbox"
                      checked={newCarrier.contactTypes.includes('email')}
                      onChange={() => handleToggleContactType('email')}
                      disabled={isSubmitting}
                    />
                    <span>📧 Email</span>
                  </label>
                  <div className="contact-type-disabled-group">
                    <label className="contact-type-checkbox contact-type-checkbox--disabled">
                      <input type="checkbox" checked={false} disabled />
                      <span>📞 Phone</span>
                    </label>
                    <label className="contact-type-checkbox contact-type-checkbox--disabled">
                      <input type="checkbox" checked={false} disabled />
                      <span>🌐 Website</span>
                    </label>
                    <label className="contact-type-checkbox contact-type-checkbox--disabled">
                      <input type="checkbox" checked={false} disabled />
                      <span>🔗 API</span>
                    </label>
                    <span className="coming-soon-badge">Coming soon</span>
                  </div>
                </div>
              </div>

              {newCarrier.contactTypes.length > 1 && (
                <div className="modal-form-group">
                  <label>Primary Contact Type *</label>
                  <select
                    value={newCarrier.primaryContactType}
                    onChange={(e) => setNewCarrier({ ...newCarrier, primaryContactType: e.target.value })}
                    disabled={isSubmitting}
                  >
                    <option value="">Select primary contact type...</option>
                    {newCarrier.contactTypes.includes('email') && <option value="email">📧 Email</option>}
                    {newCarrier.contactTypes.includes('phone') && <option value="phone">📞 Phone</option>}
                    {newCarrier.contactTypes.includes('website') && <option value="website">🌐 Website</option>}
                    {newCarrier.contactTypes.includes('api') && <option value="api">🔗 API</option>}
                  </select>
                </div>
              )}

              {(newCarrier.contactTypes.includes('email') || newCarrier.contactTypes.includes('phone')) && (
                <div className="modal-form-group">
                  <label>Contact Name *</label>
                  <input
                    type="text"
                    value={newCarrier.contacts?.email?.name || newCarrier.contacts?.phone?.name || ''}
                    onChange={(e) => {
                      const val = e.target.value
                      setNewCarrier(prev => ({
                        ...prev,
                        contacts: {
                          ...prev.contacts,
                          email: { ...prev.contacts.email, name: val },
                          phone: { ...prev.contacts.phone, name: val },
                        }
                      }))
                    }}
                    placeholder="John Doe"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {newCarrier.contactTypes.includes('email') && (
                <div className="modal-form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    value={newCarrier.contacts.email.address}
                    onChange={(e) => handleContactChange('email', 'address', e.target.value)}
                    placeholder="quotes@carrier.com"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {newCarrier.contactTypes.includes('phone') && (
                <div className="modal-form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={newCarrier.contacts.phone.number}
                    onChange={(e) => handleContactChange('phone', 'number', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {newCarrier.contactTypes.includes('website') && (
                <>
                  <div className="modal-form-group">
                    <label>Website URL *</label>
                    <input
                      type="url"
                      value={newCarrier.contacts.website.url}
                      onChange={(e) => handleContactChange('website', 'url', e.target.value)}
                      placeholder="https://www.carrier.com/contact"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="modal-form-group">
                    <label>Login Username</label>
                    <input
                      type="text"
                      value={newCarrier.contacts.website.username}
                      onChange={(e) => handleContactChange('website', 'username', e.target.value)}
                      placeholder="username (optional)"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="modal-form-group">
                    <label>Login Password</label>
                    <input
                      type="password"
                      value={newCarrier.contacts.website.password}
                      onChange={(e) => handleContactChange('website', 'password', e.target.value)}
                      placeholder="(optional)"
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}

              {newCarrier.contactTypes.includes('api') && (
                <>
                  <div className="modal-form-group">
                    <label>API URL *</label>
                    <input
                      type="url"
                      value={newCarrier.contacts.api.url}
                      onChange={(e) => handleContactChange('api', 'url', e.target.value)}
                      placeholder="https://api.carrier.com/quotes"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="modal-form-group">
                    <label>API Headers (JSON)</label>
                    <textarea
                      value={newCarrier.contacts.api.headers}
                      onChange={(e) => handleContactChange('api', 'headers', e.target.value)}
                      placeholder='{"Authorization": "Bearer token", "Content-Type": "application/json"}'
                      rows="3"
                      className="api-headers-textarea"
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => { setShowAddModal(false); resetForm(); }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSaveCarrier}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? (editingCarrier ? 'Saving...' : 'Adding...')
                  : (editingCarrier ? 'Save Changes' : 'Add Carrier')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Carriers
